import packageJson from 'package.json'
import { v4 as uuidv4 } from 'uuid'
import { Environment } from '~/types/EnvironmentTypes'

export const isElectron = !!window.electron

if (isElectron) {
  console.log(`⚡️ Running in electron`)
}

export const isProductionBuildEnvironment = () => {
  return (
    location.host === 'app.mangovoice.com' ||
    location.origin === 'mangofiles://bundle'
  )
}

export const isStagingBuildEnvironment = () => {
  const hostParts = location.host.split('.')

  if (location.host === 'app-staging.mangovoice.com') {
    return true
  }

  if (
    hostParts.length === 4 &&
    hostParts.slice(1).join('.') === 'app-preview.mangovoice.com'
  ) {
    return true
  }

  return false
}

export const isLocalBuildEnvironment = () => {
  return location.host.startsWith('localhost:300') // Allow for 300x range.
}

export const getBuildEnvironment = () => {
  if (isProductionBuildEnvironment()) {
    return 'production'
  }

  if (isStagingBuildEnvironment()) {
    return 'staging'
  }

  if (isLocalBuildEnvironment()) {
    return 'local'
  }

  console.error('ValueError: Could not determine build environment')

  return 'production' // assume production as it should have the most protections
}

export const getApiEnvironment = () => {
  if (localStorage.getItem('api_env')) {
    return localStorage.getItem('api_env') as Environment
  }

  if (isStagingBuildEnvironment()) {
    return Environment.STAGING
  }

  if (isLocalBuildEnvironment()) {
    return Environment.LOCAL
  }

  return Environment.PRODUCTION
}

export const getCloudwatchLogGroupName = () => {
  if (isElectron) {
    if (isLocalBuildEnvironment() || isStagingBuildEnvironment()) {
      return '/mango/desktop-app-staging/user.log'
    }
    return '/mango/desktop-app-production/user.log'
  } else {
    if (isLocalBuildEnvironment()) {
      return '/mango/web-app-staging/user.log'
    }

    if (isStagingBuildEnvironment()) {
      return '/mango/web-app-staging/user.log'
    }
    return '/mango/web-app-production/user.log'
  }
}

export const getCognitoIdentityPoolId = () => {
  if (isElectron) {
    if (isLocalBuildEnvironment() || isStagingBuildEnvironment()) {
      return 'us-west-2:f22d182f-0f88-4bd2-a254-c236aa0b1a08'
    }
    return 'us-west-2:1bcb6481-9699-4d0e-a187-b9744b83a1eb'
  } else {
    if (isLocalBuildEnvironment()) {
      return 'us-west-2:e1faf135-e34b-4e6c-8d55-7940fd09004d'
    }

    if (isStagingBuildEnvironment()) {
      return 'us-west-2:e1faf135-e34b-4e6c-8d55-7940fd09004d'
    }

    return 'us-west-2:72522132-e298-4109-98c9-612b4ab80dc2'
  }
}
export const setApiEnvironment = (toEnvironment: Environment) => {
  if (isProductionBuildEnvironment()) {
    return
  }

  localStorage.setItem('api_env', toEnvironment)
}

export const getAppVersion = async (): Promise<string> => {
  const fallbackVersion = '0.0.0'

  if (isElectron) {
    try {
      if (window.electron?.invoke) {
        return await window.electron.invoke('get-app-version')
      } else {
        console.warn(
          'Electron environment detected, but invoke method is not available.',
        )
        return fallbackVersion
      }
    } catch (error) {
      console.error('Failed to get app version via Electron IPC:', error)
      return fallbackVersion
    }
  } else {
    return packageJson.version || fallbackVersion
  }
}

export const appVersion = packageJson.version
export const appEnvironment = getApiEnvironment()
export const buildEnvironment = getBuildEnvironment()
export const cloudwatchLogGroup = getCloudwatchLogGroupName()

export const getApiDocsUrl = () => {
  if (isLocalBuildEnvironment()) {
    return undefined
  }

  const apiEnv = getApiEnvironment()
  const url =
    apiEnv === Environment.PRODUCTION
      ? 'https://api.mangovoice.com/api/schema/swagger-ui/#/'
      : 'https://api-staging.mangovoice.com/api/schema/swagger-ui/#/'

  return url
}

export const isValidEmail = (email: string) => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
  return emailRegex.test(email)
}

export const formatFileSize = (bytes: number, decimals = 2) => {
  if (bytes <= 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

interface TimeWeightedProgressTimerParams {
  minTimeMs: number
  intervals?: number
  getRawProgress: () => number
  onProgressUpdated: (progress: number) => void
  onFinished?: () => void
  fauxEndWaitTimeMs?: number
}
/**
 * @description Takes a raw progress value and applies a time-blend based on a time minimum.  Calls onFinished when progress reaches 1 after the fauxEndWaitTimeMs interval.  Ensure "rawProgress" is a value between 0 and 1.
 * @description Example - a request completes within 100ms, but we want to show a progress bar for at least 500ms - this function will blend the progress values together over that time.  You can also pass in a fauxEndWaitTimeMs to prevent the progress bar from jumping to 100% too quickly.
 * @param minTimeMs
 * @param getRawProgress
 * @param onProgressUpdated
 * @param onFinished
 * @param fauxEndWaitTimeMs
 */
export const startTimeWeightedProgressTimer = ({
  minTimeMs,
  getRawProgress,
  onProgressUpdated,
  onFinished,
  fauxEndWaitTimeMs = 750,
}: TimeWeightedProgressTimerParams) => {
  let steps = 0
  const intervals = 10
  const intervalTime = minTimeMs / intervals
  const intervalId = setInterval(() => {
    steps++
    const timeProgressIncrement = (steps * intervalTime) / minTimeMs
    const rawProgress = getRawProgress()
    const maxStepValue = intervalTime
    const blend = Math.min(maxStepValue, timeProgressIncrement * rawProgress)

    onProgressUpdated(blend)

    if (blend >= 1) {
      clearInterval(intervalId)
      const fauxEndIntervalId = setInterval(() => {
        clearInterval(fauxEndIntervalId)
        onProgressUpdated(100)
        onFinished?.()
      }, fauxEndWaitTimeMs)
    }
  }, intervalTime)
}

export const getFilteredText = (text: string, filters: string[]) => {
  return filters.reduce((acc, filter) => {
    return acc.replaceAll(filter, '')
  }, text)
}

export const getSearchMatchWithFilters = (
  text: string,
  query: string,
  filters: string[],
) => {
  const stripped = getFilteredText(text, filters)
  const matchStartIndex = stripped.indexOf(query)
  if (matchStartIndex === -1) {
    return null
  }

  const originalMatchStart = text.indexOf(query[0], matchStartIndex)
  const actualStartIndex = originalMatchStart

  const originSlice = text.slice(actualStartIndex, text.length)
  let matchCount = 0
  const matchMax = query.length
  let lastIndex = 0
  for (let i = 0; i < originSlice.length; i++) {
    if (originSlice[i] === query[matchCount]) {
      matchCount++
    }
    if (matchCount === matchMax) {
      lastIndex = i
      break
    }
  }

  const sequenceSlice = originSlice.slice(0, lastIndex + 1)
  return sequenceSlice
}

export const tryGetValueOrDefault = (
  value: string | undefined,
  defaultValue?: string,
) => {
  return hasValue(value) ? value : defaultValue
}

export const hasNoValueOrIsWhitespace = (value?: string) => {
  return value === undefined || value === null || value === ''
}

export const isNumeric = (value: string) => {
  const num = Number.parseFloat(value)
  const isNan = isNaN(num)
  return !isNan
}

export const hasValue = (value?: string | unknown | number) => {
  if (typeof value === 'number') {
    return value !== undefined && value !== null
  }

  if (typeof value === 'object') {
    return value !== undefined && value !== null
  }

  return !hasNoValueOrIsWhitespace(value as string | undefined)
}

export const allHaveValue = (
  values: (string | number | unknown | undefined)[],
) => {
  return values.every(hasValue)
}

export const someHaveValue = (
  values: (string | number | unknown | undefined)[],
) => {
  return values.some(hasValue)
}

export const noneHaveValue = (
  values: (string | number | unknown | undefined)[],
) => {
  return !someHaveValue(values)
}

export const newUuid = () => {
  return uuidv4()
}

export const valueIsOneOf = (value: string, values: string[]) => {
  return values.includes(value)
}

export const getBitValueOrDefault = (value?: boolean) => {
  return value === undefined ? undefined : value ? 1 : 0
}

export const getDateFromLongOrDefault = (long?: number | string) => {
  if (!hasValue(long)) {
    return undefined
  }

  const numericValue = Number(long)
  if (isNaN(numericValue)) {
    return undefined
  }

  const dateFromLong = new Date(numericValue)
  return dateFromLong
}
